import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedRestaurant } from '../context/SelectedRestaurantContext';
import { useAuth } from '../hooks/useAuth';
import { useRestaurantCache } from '../context/RestaurantCacheContext';
import type { Restaurant as AppRestaurant } from '../types';


interface RestaurantSelectorProps {
  style?: React.CSSProperties;
  showLoading?: boolean;
  showInfo?: boolean;
  showOne?: boolean;
  onRestaurantChange?: (restaurantId: string) => void;
  isHeader?: boolean;
}

export default function RestaurantSelector({ 
  style, 
  showLoading = false, 
  showInfo = true,
  showOne = false,
  onRestaurantChange,
  isHeader = false
}: RestaurantSelectorProps) {
  const { selectedRestaurantId, selectedRestaurantName, selectedRestaurantLogo, setSelectedRestaurant, isLoading, selectedRestaurantTimezone, selectedRestaurantStatus } = useSelectedRestaurant();
  const { isOwner, isManager, isWaiter, user } = useAuth();
  const { 
    userRestaurants: cachedUserRestaurants, 
    loadingUserRestaurants, 
    loadUserRestaurants,
    accessRestaurants: cachedAccessRestaurants,
    loadingAccessRestaurants,
    loadAccessRestaurants,
    refreshRestaurants,
  } = useRestaurantCache();
  const [error] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  
  // Verificar si estamos en la página de admin/restaurants para evitar peticiones duplicadas
  const isInAdminRestaurantsPage = window.location.pathname === '/admin/restaurants';

  // Cargar restaurantes del usuario cuando sea necesario
  useEffect(() => {
    if (!user?.uid) return;

    // Cargar restaurantes del usuario en todas las páginas donde se necesiten
    const shouldLoadUserRestaurants = () => {
      const path = window.location.pathname;
      
      // Páginas donde se necesitan restaurantes
      const pagesThatNeedRestaurants = [
        '/admin',             // Dashboard principal
        '/admin/restaurants', // Página de gestión de restaurantes
        '/admin/menu',        // Página de menú
        '/admin/waiters',     // Página de meseros
        '/admin/responsibles', // Página de responsables
        '/admin/tables',      // Página de mesas
        '/admin/kitchen',     // Página de cocina
        '/admin/sales',      // Página de ventas
        '/admin/analytics',   // Página de analytics
        '/profile',           // Página de perfil (para selector de restaurantes)
        //'/client',            // Páginas cliente
        '/waiter',
        // '/',                  // Home
        //'/home'           // Home alternativo
      ];
      
      return pagesThatNeedRestaurants.some(page => path.startsWith(page));
    };

    console.log('🏪 RestaurantSelector - Debug:', {
      currentPath: window.location.pathname,
      shouldLoad: shouldLoadUserRestaurants(),
      isWaiter: isWaiter(),
      isManager: isManager(),
      userRole: isWaiter() ? 'waiter' : isManager() ? 'manager' : 'other'
    });
    
    if (shouldLoadUserRestaurants()) {
      // Para waiters y managers, cargar restaurantes con acceso
      if (isWaiter() || isManager()) {
        console.log('🏪 RestaurantSelector - Cargando restaurantes con acceso para waiter/manager');
        loadAccessRestaurants(user.uid, user.email || undefined);
      } else {
        // Para owners y admins, cargar restaurantes del usuario
        console.log('🏪 RestaurantSelector - Cargando restaurantes del usuario');
        loadUserRestaurants(user.uid, user.email || undefined);
      }
    } else {
      console.log('🏪 RestaurantSelector - No cargando restaurantes en esta página');
    }
      }, [user?.uid, loadUserRestaurants, loadAccessRestaurants, window.location.pathname, isWaiter, isManager]);

  // Seleccionar automáticamente el primer restaurante si no hay uno seleccionado
  useEffect(() => {
    // Determinar qué lista de restaurantes usar según el rol
    const restaurantsToUse = (isWaiter() || isManager()) ? cachedAccessRestaurants : cachedUserRestaurants;
    
    if (restaurantsToUse.length > 0 && !selectedRestaurantId && user?.uid) {
      const firstRestaurant = restaurantsToUse[0];
      console.log('🏪 RestaurantSelector - Seleccionando automáticamente el primer restaurante:', firstRestaurant.name);
      
      setSelectedRestaurant(
        firstRestaurant.id,
        firstRestaurant.name,
        firstRestaurant.image,
        'open', // Por defecto abierto
        firstRestaurant.timezone || null
      ).then(() => {
        onRestaurantChange?.(firstRestaurant.id);
      }).catch((error) => {
        console.error('Error seleccionando restaurante automáticamente:', error);
      });
    }
  }, [cachedUserRestaurants, cachedAccessRestaurants, selectedRestaurantId, user?.uid, setSelectedRestaurant, onRestaurantChange, isWaiter, isManager]);

  const handleRestaurantSelect = async (restaurant: AppRestaurant) => {
    try {
      await setSelectedRestaurant(
        restaurant.id,
        restaurant.name,
        restaurant.image,
        'open', // Por defecto abierto
        restaurant.timezone || null
      );
      onRestaurantChange?.(restaurant.id);
      setShowSelector(false); // Cerrar selector después de seleccionar
    } catch (error) {
      console.error('Error seleccionando restaurante:', error);
    }
  };

  const toggleSelector = () => {
    // Determinar qué lista de restaurantes usar según el rol
    const restaurantsToUse = (isWaiter() || isManager()) ? cachedAccessRestaurants : cachedUserRestaurants;
    
          console.log('🔄 Toggle selector:', {
        showSelector: !showSelector,
        restaurantsCount: restaurantsToUse.length,
        currentRestaurants: restaurantsToUse.map(r => r.name),
        userRole: isWaiter() ? 'waiter' : isManager() ? 'manager' : 'other'
      });
    
    if (restaurantsToUse.length > 1) {
      setShowSelector(!showSelector);
    } else if (restaurantsToUse.length === 1) {
      // Si solo hay un restaurante, seleccionarlo automáticamente
      const restaurant = restaurantsToUse[0];
      handleRestaurantSelect(restaurant);
    }
  };

  const handleRefreshClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!user?.uid || isRefreshing) return;
    try {
      setIsRefreshing(true);
      await refreshRestaurants(user.uid, user.email || undefined);
      // Forzar recarga explícita según rol para reflejar cambios de inmediato
      if (isWaiter() || isManager()) {
        await loadAccessRestaurants(user.uid, user.email || undefined);
      } else {
        await loadUserRestaurants(user.uid, user.email || undefined);
      }
    } catch (err) {
      // no-op, ya hay logs en el contexto
    } finally {
      setIsRefreshing(false);
    }
  };
  // Modal local eliminado; usaremos el modal global ya existente (AdminLayout) vía evento

  // Mostrar estado de carga (considerar ambos por seguridad)
  const isLoadingRestaurants = (isWaiter() || isManager()) 
    ? loadingAccessRestaurants || loadingUserRestaurants 
    : loadingUserRestaurants || loadingAccessRestaurants;
  
  if (isLoadingRestaurants || isLoading || showLoading || isRefreshing) {
    return (
      <div className={`w-full flex items-center ${!isHeader ? 'mb-4' : ''}`} style={style}>
        <div className="flex items-center justify-center gap-2 sm:gap-3 py-3 sm:py-4">
          <div className="relative">
            <div className="w-4 h-4 sm:w-6 sm:h-6 border-3 sm:border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-4 h-4 sm:w-6 sm:h-6 border-3 sm:border-4 border-transparent border-t-[#2C4358] rounded-full animate-spin" style={{animationDelay: '0.5s'}}></div>
          </div>
          <span className="text-xs sm:text-sm font-bold text-[#2C4358]">Cargando restaurantes...</span>
        </div>
      </div>
    );
  }

  // Determinar qué restaurantes mostrar según la página y el tipo de usuario
  const getDisplayRestaurants = () => {
    // Para waiters y managers, usar restaurantes con acceso
    if (isWaiter() || isManager()) {
      return cachedAccessRestaurants;
    }
    // Para owners/admins: preferir userRestaurants, pero si llegan vacíos usa access como respaldo
    return (cachedUserRestaurants && cachedUserRestaurants.length > 0)
      ? cachedUserRestaurants
      : cachedAccessRestaurants;
  };

  const displayRestaurants = getDisplayRestaurants();

  // Mostrar error
  if (error) {
    return (
      <div className={`w-full flex items-center ${!isHeader ? 'mb-4' : ''}`} style={style}>
        <div className="text-center py-3 sm:py-4">
          <div className="text-xs sm:text-sm font-bold text-red-600 mb-2 sm:mb-3 flex items-center justify-center gap-1 sm:gap-2">
            <span className="text-lg sm:text-xl">❌</span>
            <span>{error}</span>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white font-black rounded-xl sm:rounded-2xl hover:from-[#1a8f8c] hover:to-[#1e2f3f] transition-all duration-300 transform hover:scale-105 shadow-lg sm:shadow-xl relative overflow-hidden group text-xs sm:text-sm"
          >
            {/* Efecto de brillo */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            <span className="relative z-10">🔄 Reintentar</span>
          </button>
        </div>
      </div>
    );
  }

  // Si no tiene permisos para ver restaurantes, mostrar solo el nombre del restaurante
  const userCanAccessRestaurants = isOwner() || isManager() || isWaiter();
  if (!userCanAccessRestaurants) {
    return (
      <div className={`w-full flex items-center justify-center ${!isHeader ? 'mb-4' : ''}`} style={style}>
        {selectedRestaurantName ? (
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 px-3 sm:px-4 py-2 rounded-xl sm:rounded-2xl border-2 border-[#22B6B2]/30 max-w-full">
            {selectedRestaurantLogo ? (
              <img 
                src={selectedRestaurantLogo} 
                alt={selectedRestaurantName}
                className="w-6 h-6 sm:w-8 sm:h-8 rounded-full object-cover"
              />
            ) : (
              <span className="text-[#22B6B2] text-base sm:text-lg">🏪</span>
            )}
            <div className={`${isHeader ? 'flex items-center gap-1 min-w-0' : 'flex flex-col leading-tight min-w-0'}`}>
              <span className="text-xs sm:text-sm font-bold text-[#2C4358] truncate">
                {selectedRestaurantName}
              </span>
              {selectedRestaurantTimezone?.name && (
                isHeader ? (
                  <span className="text-[10px] text-[#22B6B2] truncate hidden sm:inline">· {selectedRestaurantTimezone.name}</span>
                ) : (
                  <span className="text-[10px] sm:text-[11px] text-[#22B6B2] truncate">{selectedRestaurantTimezone.name}</span>
                )
              )}
            </div>
          </div>
        ) : (
          <span className="text-xs sm:text-sm font-bold text-[#85929e] italic">
            Sin restaurante
          </span>
        )}
      </div>
    );
  }

  // Mostrar mensaje cuando no hay restaurantes
  if (displayRestaurants.length === 0) {
    return (
      <div className={`w-full flex items-center ${!isHeader ? 'mb-2' : ''}`} style={style}>
        <button
          type="button"
          onClick={() => navigate('/admin/restaurants')}
          className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-[11px] sm:text-xs transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 ${
            isHeader 
              ? 'bg-white/20 text-white border-white/30 hover:bg-white/30'
              : 'bg-transparent text-[#85929e] border-[#22B6B2]/20 hover:border-[#22B6B2]/40 hover:bg-[#22B6B2]/5'
          }`}
          title={isOwner() ? 'Crear restaurante' : 'Ir a restaurantes'}
          aria-label={isOwner() ? 'Crear restaurante' : 'Ir a restaurantes'}
        >
          <span className={`${isHeader ? 'text-white' : 'text-[#22B6B2]'} text-xs`}>🏪</span>
          <span className="truncate max-w-[220px] sm:max-w-[280px]">
            {isOwner()
              ? 'Ir a restaurantes'
              : ''}
          </span>
        </button>
      </div>
    );
  }

  // Solo mostrar selector si hay más de un restaurante
  if (displayRestaurants.length <= 1 && showOne) {
    return null;
  }

  // Si solo hay un restaurante, mostrar solo el nombre (y botón abrir/cerrar)
  if (displayRestaurants.length === 1) {
    const restaurant = displayRestaurants[0];
    const isOpenSingle = selectedRestaurantStatus
      ? selectedRestaurantStatus === 'open'
      : (typeof (restaurant as any)?.isOpen === 'boolean' ? (restaurant as any).isOpen : false);
    return (
      <div className={`w-full flex items-center justify-center ${!isHeader ? 'mb-4' : ''}`} style={style}>
        <div className={`relative overflow-hidden flex items-center justify-center ${isHeader ? 'space-x-1 px-2 py-1 pr-10' : 'space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 sm:py-3 pr-12'} rounded-xl sm:rounded-2xl border-2 ${
          isHeader 
            ? 'bg-white/20 border-white/30' 
            : 'bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 border-[#22B6B2]/30'
        }`}>
          {/* Botón abrir/cerrar en vista de un solo restaurante - solo Owner/Manager */}
          {(isOwner() || isManager()) && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new Event('ots:openStatusModal')); }}
              title={isOpenSingle ? 'Cerrar restaurante' : 'Abrir restaurante'}
              aria-label={isOpenSingle ? 'Cerrar restaurante' : 'Abrir restaurante'}
              className={`absolute ${isHeader ? 'right-1' : 'right-2'} top-1/2 -translate-y-1/2 z-10 w-auto px-2 h-8 sm:h-9 rounded-full border shadow-sm flex items-center justify-center text-[11px] sm:text-xs font-semibold transition-colors ${
                isOpenSingle
                  ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                  : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
              }`}
            >
              {isOpenSingle ? 'Cerrar' : 'Abrir'}
            </button>
          )}

          {restaurant.image ? (
            <img 
              src={restaurant.image} 
              alt={restaurant.name}
              className={`${isHeader ? 'w-5 h-5' : 'w-6 h-6 sm:w-8 sm:h-8'} rounded-full object-cover`}
            />
          ) : (
            <span className={`text-lg sm:text-xl ${
              isHeader ? 'text-white' : 'text-[#22B6B2]'
            }`}>🏪</span>
          )}
          <div className={`${isHeader ? 'flex items-center gap-1 min-w-0' : 'flex flex-col leading-tight min-w-0'}`}>
            <span className={`font-bold ${isHeader ? 'text-xs' : 'text-xs sm:text-sm'} truncate ${
              isHeader ? 'text-white' : 'text-[#2C4358]'
            }`}>
              {restaurant.name}
            </span>
            {restaurant.timezone?.name && (
              isHeader ? (
                <span className={`text-[10px] truncate ${isHeader ? 'text-white/80' : 'text-[#22B6B2]'} hidden sm:inline`}>· {restaurant.timezone.name}</span>
              ) : (
                <span className={`text-[10px] sm:text-[11px] truncate text-[#22B6B2]`}>
                  {restaurant.timezone.name}
                </span>
              )
            )}
          </div>
          {restaurant.status && (
            <div className={`${isHeader ? 'w-2 h-2' : 'w-2 h-2 sm:w-3 sm:h-3'} rounded-full shadow-lg ${restaurant.status === 'active' ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-red-400 to-red-600'}`}></div>
          )}
        </div>
      </div>
    );
  }

  // Mostrar restaurante seleccionado con opción de cambiar
  const selectedRestaurant = displayRestaurants.find(r => r.id === selectedRestaurantId) || displayRestaurants[0];

  console.log('🏪 RestaurantSelector render:', {
    restaurants: displayRestaurants.length,
    selectedRestaurant: selectedRestaurant?.name,
    selectedRestaurantId,
    showSelector,
    userCanAccessRestaurants: isOwner() || isManager() || isWaiter(),
    isInAdminRestaurantsPage,
    displayRestaurants: displayRestaurants.map(r => ({ id: r.id, name: r.name })),
    userRole: { isOwner: isOwner(), isManager: isManager(), isWaiter: isWaiter() },
    localStorageData: {
      hasLocalStorage: typeof window !== 'undefined' && window.localStorage,
      accessRestaurantsKey: typeof window !== 'undefined' ? localStorage.getItem('ots_access_restaurants') : null,
      timestamp: typeof window !== 'undefined' ? localStorage.getItem('ots_access_restaurants_timestamp') : null
    },
    stateData: {
      restaurantsLength: cachedUserRestaurants.length,
      cachedAccessRestaurantsLength: cachedUserRestaurants.length,
      cachedUserRestaurantsLength: cachedUserRestaurants.length
    }
  });

  // Estado abierto/cerrado del restaurante seleccionado
  // Prioriza el status del contexto (se actualiza al confirmar en el modal global)
  const isOpenNow = selectedRestaurantStatus
    ? selectedRestaurantStatus === 'open'
    : (typeof selectedRestaurant?.isOpen === 'boolean' ? (selectedRestaurant as any).isOpen : false);

  return (
    <div className={`w-full flex items-center ${!isHeader ? 'mb-4' : ''}`} style={style}>
      <div className={`relative ${isHeader ? 'w-full max-w-xs' : 'w-full'}`}>
        {/* Botón de refresco compacto y responsive */}
        <button
          type="button"
          onClick={handleRefreshClick}
          disabled={isRefreshing}
          title="Refrescar restaurantes"
          aria-label="Refrescar restaurantes"
          className={`absolute ${isHeader ? 'top-1 right-1' : 'top-2 right-2'} z-10 w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 bg-white/80 backdrop-blur-md text-[#2C4358] border-[#22B6B2]/30 hover:border-[#22B6B2]/50 hover:bg-white shadow-md flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <span className={`${isRefreshing ? 'animate-spin' : ''} text-sm sm:text-base`}>
            ↻
          </span>
        </button>
        {/* Botón abrir/cerrar - solo Owner/Manager */}
        {(isOwner() || isManager()) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new Event('ots:openStatusModal')); }}
            title={isOpenNow ? 'Cerrar restaurante' : 'Abrir restaurante'}
            aria-label={isOpenNow ? 'Cerrar restaurante' : 'Abrir restaurante'}
            className={`absolute ${isHeader ? 'top-1 right-10' : 'top-2 right-12'} z-10 w-auto px-2 h-8 sm:h-9 rounded-full border shadow-sm flex items-center justify-center text-[11px] sm:text-xs font-semibold transition-colors ${
              isOpenNow
                ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
            }`}
          >
            {isOpenNow ? 'Cerrar' : 'Abrir'}
          </button>
        )}
        {/* Vista principal: restaurante seleccionado */}
        {!showSelector && (
          <button
            onClick={toggleSelector}
            className={`w-full flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 cursor-pointer group shadow-lg hover:shadow-xl ${
              isHeader 
                ? 'bg-white/20 border-white/30 hover:bg-white/30 hover:border-white/50 px-2 py-1 space-x-1' 
                : 'bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 space-x-2 sm:space-x-3'
            }`}
          >
            {selectedRestaurant?.image ? (
              <img 
                src={selectedRestaurant.image} 
                alt={selectedRestaurant.name}
                className={`rounded-full object-cover group-hover:scale-110 transition-transform duration-300 ${
                  isHeader ? 'w-5 h-5' : 'w-6 h-6 sm:w-8 sm:h-8'
                }`}
              />
            ) : (
              <span className={`group-hover:scale-110 transition-transform duration-300 ${
                isHeader ? 'text-white text-sm' : 'text-[#22B6B2] text-lg sm:text-xl'
              }`}>🏪</span>
            )}
            <div className={`${isHeader ? 'flex items-center gap-1 flex-1 min-w-0' : 'flex flex-col flex-1 leading-tight min-w-0'}`}>
              <span className={`font-bold text-xs sm:text-sm truncate ${
                isHeader ? 'text-white text-xs' : 'text-[#2C4358]'
              }`}>
                {selectedRestaurant?.name || 'Seleccionar restaurante'}
              </span>
              {selectedRestaurant?.timezone?.name && (
                isHeader ? (
                  <span className="text-[10px] truncate text-white/80 hidden sm:inline">· {selectedRestaurant.timezone.name}</span>
                ) : (
                  <span className={`text-[10px] sm:text-[11px] truncate text-[#22B6B2]`}>
                    {selectedRestaurant.timezone.name}
                  </span>
                )
              )}
            </div>
            {selectedRestaurant?.status && (
              <div className={`rounded-full shadow-lg ${selectedRestaurant.status === 'active' ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-red-400 to-red-600'} ${
                isHeader ? 'w-2 h-2' : 'w-2 h-2 sm:w-3 sm:h-3'
              }`}></div>
            )}
            <span className={`text-xs group-hover:rotate-180 transition-transform duration-300 ${
              isHeader ? 'text-white' : 'text-[#22B6B2]'
            }`}>▼</span>
          </button>
        )}

        {/* Overlay y selector expandido */}
        {showSelector && (
          <div className="fixed inset-0 bg-slate-700/60 backdrop-blur-sm flex items-start justify-center pt-8 sm:pt-12 lg:pt-16 z-50">
            <div className="bg-white/90 backdrop-blur-md border-2 border-[#22B6B2]/20 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl p-4 sm:p-6 w-full max-w-full relative overflow-hidden lg:max-w-[70vw] lg:mx-auto">
              {/* Fondo animado sutil */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2]/5 via-[#2C4358]/5 to-[#85929e]/5 animate-pulse"></div>
              
              {/* Header del selector */}
              <div className="relative z-10 flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <span className="text-lg sm:text-xl">🏪</span>
                  <span className="text-sm sm:text-lg font-black text-[#2C4358]">
                    Seleccionar Restaurante ({displayRestaurants.length})
                  </span>
                </div>
                <button
                  onClick={toggleSelector}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-r from-[#22B6B2]/10 to-[#2C4358]/10 hover:from-[#22B6B2]/20 hover:to-[#2C4358]/20 text-[#2C4358] hover:text-[#22B6B2] text-lg sm:text-xl transition-all duration-300 border-2 border-[#22B6B2]/30 hover:border-[#22B6B2]/50 hover:scale-110 group"
                >
                  <span className="group-hover:rotate-90 transition-transform duration-300">✕</span>
                </button>
              </div>

              {/* Carousel de restaurantes tipo 3D - Responsive */}
              <div className="relative z-10">
                {/* En móvil: scroll vertical */}
                <div className="block sm:hidden">
                  <div className="max-h-[60vh] overflow-y-auto scrollbar-hide">
                    <div className="grid grid-cols-1 gap-3">
                      {displayRestaurants.map((restaurant) => (
                        <button
                          key={restaurant.id}
                          className={`relative p-4 rounded-xl border-2 transition-all duration-300 overflow-hidden
                            ${selectedRestaurantId === restaurant.id
                              ? 'bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white border-[#22B6B2] shadow-lg'
                              : 'bg-white text-[#2C4358] border-[#22B6B2]/30 hover:border-[#22B6B2] hover:bg-white'
                            }
                          `}
                          onClick={() => handleRestaurantSelect(restaurant)}
                        >
                          {/* Efecto de brillo */}
                          <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 transition-all duration-1000
                            ${selectedRestaurantId === restaurant.id ? 'animate-pulse' : 'opacity-0'}
                          `}></div>
                          
                          <div className="relative z-10 flex items-center space-x-3">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 transition-transform duration-300
                              ${selectedRestaurantId === restaurant.id ? 'scale-110' : 'group-hover:scale-110'}
                            `}>
                              {restaurant.image ? (
                                <img 
                                  src={restaurant.image} 
                                  alt={restaurant.name}
                                  className="w-full h-full rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-2xl">🏪</span>
                              )}
                            </div>
                            <div className="flex-1 text-left">
                              <h3 className={`font-bold text-sm transition-all duration-300
                                ${selectedRestaurantId === restaurant.id ? 'text-white' : 'text-[#2C4358]'}
                              `}>
                                {restaurant.name}
                              </h3>
                              {restaurant.timezone?.name && (
                                <div className="mt-0.5">
                                  <span className={`text-[10px] font-medium text-[#22B6B2]`}>
                                    {restaurant.timezone.name}
                                  </span>
                                </div>
                              )}
                              {restaurant.status && (
                                <div className="flex items-center space-x-2 mt-1">
                                  <div className={`w-2 h-2 rounded-full shadow-sm ${restaurant.status === 'active' ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-red-400 to-red-600'}`}></div>
                                  <span className={`text-xs font-bold ${selectedRestaurantId === restaurant.id ? 'text-white' : 'text-[#85929e]'}`}>
                                    {restaurant.status === 'active' ? 'Activo' : 'Inactivo'}
                                  </span>
                                </div>
                              )}
                            </div>
                            {selectedRestaurantId === restaurant.id && (
                              <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* En tablet y desktop: carousel horizontal elegante */}
                <div className="hidden sm:block">
                  <div className="relative">
                    <div className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide snap-x snap-mandatory px-8 lg:px-16">
                      {displayRestaurants.map((restaurant) => (
                        <button
                          key={restaurant.id}
                          className={`min-w-[220px] max-w-[260px] h-auto flex-shrink-0 snap-center transform transition-all duration-500 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#22B6B2]/30
                            ${selectedRestaurantId === restaurant.id
                              ? 'scale-105 shadow-2xl'
                              : 'hover:scale-102 shadow-lg'
                            }
                          `}
                          onClick={() => handleRestaurantSelect(restaurant)}
                        >
                          <div className={`relative p-5 rounded-2xl border-2 transition-all duration-300 overflow-hidden w-full h-full flex flex-col items-center justify-center
                            ${selectedRestaurantId === restaurant.id
                              ? 'bg-gradient-to-br from-[#22B6B2] to-[#2C4358] text-white border-[#22B6B2] shadow-[#22B6B2]/50'
                              : 'bg-white text-[#2C4358] border-[#22B6B2]/30 hover:border-[#22B6B2] hover:bg-white'
                            }
                          `}>
                            {/* Efecto de brillo */}
                            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 transition-all duration-1000
                              ${selectedRestaurantId === restaurant.id ? 'animate-pulse' : 'opacity-0'}
                            `}></div>
                            <div className="relative z-10 flex flex-col items-center justify-center h-full space-y-2">
                              <div className={`w-12 h-12 sm:w-16 sm:h-16 mb-1 transition-transform duration-300
                                ${selectedRestaurantId === restaurant.id ? 'scale-110' : 'group-hover:scale-110'}
                              `}>
                                {restaurant.image ? (
                                  <img 
                                    src={restaurant.image} 
                                    alt={restaurant.name}
                                    className="w-full h-full rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="text-3xl">🏪</span>
                                )}
                              </div>
                              <h3 className={`font-bold text-base transition-all duration-300 break-words line-clamp-2 leading-tight text-center
                                ${selectedRestaurantId === restaurant.id ? 'text-white' : 'text-[#2C4358]'}
                              `}>
                                {restaurant.name}
                              </h3>
                              {restaurant.timezone?.name && (
                                <div className="mt-1">
                                  <span className="text-[11px] font-medium text-[#22B6B2]">{restaurant.timezone.name}</span>
                                </div>
                              )}
                              {restaurant.status && (
                                <div className="flex items-center justify-center space-x-2 mt-1">
                                  <div className={`w-3 h-3 rounded-full shadow-lg ${restaurant.status === 'active' ? 'bg-gradient-to-r from-green-400 to-green-600' : 'bg-gradient-to-r from-red-400 to-red-600'}`}></div>
                                  <span className={`text-xs font-bold ${selectedRestaurantId === restaurant.id ? 'text-white' : 'text-[#85929e]'}`}>
                                    {restaurant.status === 'active' ? 'Activo' : 'Inactivo'}
                                  </span>
                                </div>
                              )}
                              {selectedRestaurantId === restaurant.id && (
                                <div className="flex justify-center pt-1">
                                  <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    {/* Indicadores de scroll opcionales */}
                    <div className="hidden lg:flex justify-center space-x-2 mt-4">
                      {displayRestaurants.map((_, index) => (
                        <div key={index} className="w-2 h-2 bg-[#85929e] rounded-full"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Información adicional */}
              {showInfo && (
                <div className="relative z-10 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-[#22B6B2]/20">
                  <div className="text-xs sm:text-sm font-bold text-[#85929e] text-center">
                    {displayRestaurants.length} restaurante{displayRestaurants.length > 1 ? 's' : ''} disponible{displayRestaurants.length > 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Modal local removido; se usa el modal global en AdminLayout (evento ots:openStatusModal) */}
    </div>
  );
} 